import { type CSSProperties, type ReactNode } from 'react';
import styles from './PaneShell.module.css';

// ============================================================================
// PaneShell (E2/Paket C) — das Vier-Zonen-Grid der Shell (docs/30 §4/§7.5).
// ----------------------------------------------------------------------------
// Rendert IMMER alle Zonen (Rail, Buehne, Schirm, Dock). Verborgene Zonen
// bekommen Spaltenbreite/Zeilenhoehe 0 — NIE display:none, NIE conditional
// render (sonst Remount beim Live-Resize, §4). Die Spaltenmasse kommen aus den
// Tokens der Breitenachse (--pane-rail/-inspector/-dock, styles/layout.css) —
// deshalb KEINE eigene @media-Query hier (der Breakpoint-Ratchet bleibt bei 22).
//
// Logische Grid-Achsen (grid-template-areas mit inline-start/-end-Semantik),
// damit Rail und Schirm in RTL-Locales die Seiten tauschen, ohne zweite Grid-
// Definition. Ausnahme: die Fenstercontrols-Inset bleibt physisch links (§6).
//
// In E2 tragen Schirm und Dock keinen Inhalt (inspectorOpen=false, kein dock) →
// ihre Tracks sind 0; sichtbar bleibt „Rail | Buehne". Die leeren Zonen sind
// trotzdem im DOM (Struktur stabil fuer E5a'/E7).
// ============================================================================

export interface PaneShellProps {
  variant: 'game' | 'build';
  rail?: ReactNode;
  /** Buehne (Spielshell) bzw. Inhalt (Baushell). */
  stage: ReactNode;
  /** Viewport-fixe Shell-Ebene, die denselben lokalen Rail-Offset erbt. */
  backdrop?: ReactNode;
  inspector?: ReactNode;
  dock?: ReactNode;
  inspectorOpen?: boolean;
}

export function PaneShell({
  variant,
  rail,
  stage,
  backdrop,
  inspector,
  dock,
  inspectorOpen = false,
}: PaneShellProps) {
  // Track-Masse: nur belegte Zonen reservieren Platz. Schirm/Dock sind in E2
  // leer → 0. Die Rail-Breite steuert --pane-rail (data-rail auf <html>).
  const gridVars = {
    // Nur eine wirklich belegte Rail-Zone darf viewport-fixe Nachfahren
    // verschieben. Ausserhalb der PaneShell existiert die Variable nicht.
    '--active-rail-offset': rail != null ? 'var(--pane-rail, 0px)' : '0px',
    // L1: linke Kante der BUEHNE — Rail plus Spaltenluecke. Viewport-fixe
    // Ebenen, die buendig mit dem Inhalt beginnen sollen (die transparente
    // Kopfzeile), binden sich hieran statt an den reinen Rail-Offset; sonst
    // stehen sie um --pane-gap (1 px ab 600 px) daneben.
    '--active-stage-offset': rail != null
      ? 'calc(var(--pane-rail, 0px) + var(--pane-gap, 0px))'
      : '0px',
    '--col-inspector': inspectorOpen && inspector != null ? 'var(--pane-inspector)' : '0px',
    '--row-dock': dock != null ? 'var(--pane-dock)' : '0px',
  } as CSSProperties;

  return (
    <div className={styles.shell} data-variant={variant} style={gridVars}>
      {backdrop}
      {/* Rail-Zone: AppNav ist selbst das <nav>-Landmark. In der Bar-Gestalt
          ist die Zone 0 breit und AppNav position:fixed (Handy-Leiste). */}
      <div className={styles.rail}>{rail}</div>
      {/* Buehne: das <main>-Landmark kommt aus AppShell/GameShell. */}
      <div className={styles.stage}>{stage}</div>
      {/* Schirm: in E2 leer/0. aria-hidden solange geschlossen (kein leeres
          Landmark im VoiceOver-Rotor). */}
      <div className={styles.inspector} aria-hidden={!inspectorOpen || inspector == null}>
        {inspector}
      </div>
      {/* Dock: in E2 leer/0. */}
      <div className={styles.dock} aria-hidden={dock == null}>
        {dock}
      </div>
    </div>
  );
}
